// SCR-STAFF-852 (AP-INS-01؛ US-INS-050، 051، 042): مجوز قبلی (درخواست، پاسخ تأیید یا رد بیمه‌گر، تعداد مجاز و مانده) و معرفی‌نامه
// سازمان یا بیمه‌گر (خدمات مجاز یا سقف مبلغ، یک‌بارمصرف). خدمتی که طرح مجوز قبلی می‌خواهد، بدون مجوز معتبر سهم صفر با علت
// <c>NoPreAuth</c> می‌گیرد (BR-INS-012).
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import {
  unwrap,
  type InsPreAuthorizationView,
  type InsReferralUsage,
  type InsReferralView,
  type PatientSearchItem,
} from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  applyServerErrors,
  DataTable,
  EmptyState,
  faDigits,
  FormDialog,
  JalaliDate,
  JalaliDatePickerRHF,
  ListPage,
  Money,
  MoneyFieldRHF,
  SelectRHF,
  StatusChip,
  TextFieldRHF,
  zRequiredText,
  type DataTableColumn,
} from '@phoenix/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { PatientPicker } from '../patients/PatientPicker';
import { WriteNotice } from '../settings/common';
import { insuranceKeys, preAuthTone, REFERRAL_USAGES, referralTone } from './common';

export const SCR_STAFF_852 = 'SCR-STAFF-852';

export function AuthorizationsPage() {
  const { t } = useTranslation('ins');
  const access = useAccess();
  const write = useWriteAccess('ins.preauth.manage');
  const [tab, setTab] = useState<'preauth' | 'referrals'>('preauth');
  const [patient, setPatient] = useState<PatientSearchItem | null>(null);

  return (
    <ListPage
      screenId={SCR_STAFF_852}
      titleKey="ins:authorizations.title"
      subtitleKey="ins:authorizations.subtitle"
      filters={
        <Stack spacing={1}>
          <WriteNotice write={write} />
          <PatientPicker labelKey="ins:authorizations.patient" value={patient} onChange={setPatient} testId="ins-patient-picker" />
          <Tabs value={tab} onChange={(_, value: 'preauth' | 'referrals') => setTab(value)}>
            <Tab value="preauth" label={t('ins:authorizations.tab_preauth')} data-testid="ins-tab-preauth" />
            <Tab value="referrals" label={t('ins:authorizations.tab_referrals')} data-testid="ins-tab-referrals" />
          </Tabs>
        </Stack>
      }
    >
      {patient === null ? (
        <EmptyState titleKey="ins:authorizations.pick_patient" />
      ) : tab === 'preauth' ? (
        <PreAuthorizationsTable patientId={patient.id} canManage={access.can('ins.preauth.manage') && write.canWrite} />
      ) : (
        <ReferralsTable patientId={patient.id} canManage={access.can('ins.preauth.manage') && write.canWrite} />
      )}
    </ListPage>
  );
}

function PreAuthorizationsTable({ patientId, canManage }: { patientId: string; canManage: boolean }) {
  const { t } = useTranslation('ins');
  const { tenantApi, session } = useTenantSession();
  const [answering, setAnswering] = useState<InsPreAuthorizationView | null>(null);
  const authorizations = useQuery({
    queryKey: insuranceKeys.preAuthorizations(session?.tenantId, patientId),
    queryFn: () =>
      unwrap(tenantApi.GET('/api/v1/insurance/pre-authorizations', { params: { query: { patientId } } })),
  });

  const columns: DataTableColumn<InsPreAuthorizationView>[] = [
    { id: 'insurer', headerKey: 'ins:authorizations.columns.insurer', cell: (row) => row.insurerName },
    {
      id: 'status',
      headerKey: 'ins:authorizations.columns.status',
      cell: (row) => <StatusChip tone={preAuthTone(row.status)} labelKey={`ins:preauth_status.${row.status}`} />,
    },
    { id: 'number', headerKey: 'ins:authorizations.columns.number', cell: (row) => row.authorizationNumber ?? '—' },
    {
      id: 'services',
      headerKey: 'ins:authorizations.columns.services',
      cell: (row) => (
        <Stack spacing={0.25} data-testid={`ins-preauth-items-${row.preAuthId}`}>
          {row.items.map((item) => (
            <Typography key={item.serviceId} variant="body2">
              {item.serviceName || '—'}{' '}
              {t('ins:authorizations.item_counts', {
                requested: faDigits(Number(item.requestedQuantity)),
                approved: faDigits(Number(item.approvedQuantity ?? 0)),
                remaining: faDigits(Number(item.remaining)),
              })}
            </Typography>
          ))}
        </Stack>
      ),
    },
    {
      id: 'amount',
      headerKey: 'ins:authorizations.columns.amount',
      cell: (row) => (row.approvedAmount ? <Money amountMinor={Number(row.approvedAmount.amount)} /> : '—'),
      align: 'right',
    },
    {
      id: 'validity',
      headerKey: 'ins:authorizations.columns.validity',
      cell: (row) =>
        row.validFrom && row.validTo ? (
          <Stack spacing={0.25}>
            <JalaliDate value={row.validFrom} />
            <JalaliDate value={row.validTo} />
          </Stack>
        ) : (
          '—'
        ),
    },
    {
      id: 'actions',
      headerKey: 'ins:authorizations.columns.actions',
      cell: (row) =>
        canManage && row.status === 'Requested' ? (
          <Button size="small" onClick={() => setAnswering(row)} data-testid={`ins-preauth-answer-${row.preAuthId}`}>
            {t('ins:authorizations.answer')}
          </Button>
        ) : null,
    },
  ];

  return (
    <>
      <DataTable
        captionKey="ins:authorizations.caption"
        columns={columns}
        rows={authorizations.data}
        getRowId={(row) => row.preAuthId}
        isLoading={authorizations.isPending}
        error={authorizations.error}
        onRetry={() => void authorizations.refetch()}
        emptyTitleKey="ins:authorizations.empty"
      />
      {answering && <AnswerDialog authorization={answering} onClose={() => setAnswering(null)} />}
    </>
  );
}

const answerSchema = z
  .object({
    authorizationNumber: zRequiredText(64),
    approvedAmount: z.number().nullable(),
    validFrom: z.string().min(1, 'common:validation.required'),
    validTo: z.string().min(1, 'common:validation.required'),
    approvedQuantity: z.string(),
  })
  .superRefine((form, ctx) => {
    if (!(Number(form.approvedQuantity) >= 0 && Number(form.approvedQuantity) <= 1000)) {
      ctx.addIssue({ code: 'custom', path: ['approvedQuantity'], message: 'ins:authorizations.quantity_invalid' });
    }
  });

type AnswerForm = z.input<typeof answerSchema>;

/** ثبت پاسخ بیمه‌گر (AC-INS-050.2): تعداد کمتر از درخواست تأیید جزئی است و هیچ خدمت مجاز رد. */
function AnswerDialog({ authorization, onClose }: { authorization: InsPreAuthorizationView; onClose: () => void }) {
  const { t } = useTranslation('ins');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const [submitError, setSubmitError] = useState<unknown>();
  const form = useForm<AnswerForm>({
    resolver: zodResolver(answerSchema),
    defaultValues: {
      authorizationNumber: '',
      approvedAmount: null,
      validFrom: '',
      validTo: '',
      approvedQuantity: String(authorization.items[0]?.requestedQuantity ?? 1),
    },
  });

  const invalidate = async () => {
    await queryClient.invalidateQueries({
      queryKey: insuranceKeys.preAuthorizations(session?.tenantId, authorization.patientId),
    });
    onClose();
  };

  const approve = useMutation({
    mutationFn: (values: AnswerForm) =>
      unwrap(
        tenantApi.POST('/api/v1/insurance/pre-authorizations/{id}:approve', {
          params: { path: { id: authorization.preAuthId } },
            headers: { 'Idempotency-Key': idempotency.current() },
          body: {
            authorizationNumber: values.authorizationNumber,
            approvedAmount: values.approvedAmount && values.approvedAmount > 0 ? values.approvedAmount : null,
            validFrom: values.validFrom,
            validTo: values.validTo,
            items: authorization.items.map((item) => ({
              serviceId: item.serviceId,
              approvedQuantity: Number(values.approvedQuantity),
            })),
          },
        }),
      ),
    onSuccess: invalidate,
    onError: (error) => {
      const applied = applyServerErrors(error, form.setError, { fields: ['authorizationNumber', 'approvedQuantity', 'validTo'] });
      setSubmitError(applied > 0 ? undefined : error);
    },
  });

  const reject = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/insurance/pre-authorizations/{id}:reject', {
          params: { path: { id: authorization.preAuthId } },
            headers: { 'Idempotency-Key': idempotency.current() },
          body: { reasonCode: 'insurer_rejected' },
        }),
      ),
    onSuccess: invalidate,
  });

  return (
    <FormDialog
      open
      titleKey="ins:authorizations.answer_title"
      onClose={onClose}
      onSubmit={form.handleSubmit((values) => approve.mutate(values))}
      isDirty={form.formState.isDirty}
      isSubmitting={approve.isPending || reject.isPending}
      submitError={submitError ?? reject.error}
    >
      <Stack spacing={2}>
        <TextFieldRHF
          control={form.control}
          name="authorizationNumber"
          labelKey="ins:authorizations.fields.number"
          data-testid="ins-preauth-number"
        />
        <TextFieldRHF
          control={form.control}
          name="approvedQuantity"
          labelKey="ins:authorizations.fields.approved_quantity"
          helperKey="ins:authorizations.fields.approved_quantity_hint"
          type="number"
          data-testid="ins-preauth-quantity"
        />
        <span data-testid="ins-preauth-amount">
          <MoneyFieldRHF
            control={form.control}
            name="approvedAmount"
            labelKey="ins:authorizations.fields.approved_amount"
            helperKey="ins:authorizations.fields.approved_amount_hint"
            fullWidth
          />
        </span>
        <span data-testid="ins-preauth-valid-from">
          <JalaliDatePickerRHF control={form.control} name="validFrom" labelKey="ins:authorizations.fields.valid_from" />
        </span>
        <span data-testid="ins-preauth-valid-to">
          <JalaliDatePickerRHF control={form.control} name="validTo" labelKey="ins:authorizations.fields.valid_to" />
        </span>
        <Button color="error" onClick={() => reject.mutate()} disabled={reject.isPending} data-testid="ins-preauth-reject">
          {t('ins:authorizations.reject')}
        </Button>
      </Stack>
    </FormDialog>
  );
}

function ReferralsTable({ patientId, canManage }: { patientId: string; canManage: boolean }) {
  const { t } = useTranslation('ins');
  const { tenantApi, session } = useTenantSession();
  const [creating, setCreating] = useState(false);
  const referrals = useQuery({
    queryKey: insuranceKeys.referrals(session?.tenantId, patientId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/insurance/referral-letters', { params: { query: { patientId } } })),
  });

  const columns: DataTableColumn<InsReferralView>[] = [
    { id: 'issuer', headerKey: 'ins:referrals.columns.issuer', cell: (row) => row.issuer },
    { id: 'number', headerKey: 'ins:referrals.columns.number', cell: (row) => row.number },
    {
      id: 'validity',
      headerKey: 'ins:referrals.columns.validity',
      cell: (row) => (
        <Stack spacing={0.25}>
          <JalaliDate value={row.issuedOn} />
          <JalaliDate value={row.expiresOn} />
        </Stack>
      ),
    },
    {
      id: 'limit',
      headerKey: 'ins:referrals.columns.limit',
      cell: (row) =>
        row.amountLimit ? (
          <Stack spacing={0.25}>
            <Money amountMinor={Number(row.amountLimit.amount)} />
            <Typography variant="caption">
              {t('ins:referrals.consumed', { value: '' })}
              <Money amountMinor={Number(row.consumedAmount.amount)} />
            </Typography>
          </Stack>
        ) : (
          t('ins:referrals.services_only', { count: faDigits(row.serviceIds.length) })
        ),
      align: 'right',
    },
    { id: 'usage', headerKey: 'ins:referrals.columns.usage', cell: (row) => t(`ins:referral_usage.${row.usage}`) },
    {
      id: 'status',
      headerKey: 'ins:referrals.columns.status',
      cell: (row) => <StatusChip tone={referralTone(row.status)} labelKey={`ins:referral_status.${row.status}`} />,
    },
  ];

  return (
    <>
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
        {canManage && (
          <Button variant="outlined" onClick={() => setCreating(true)} data-testid="ins-referral-add">
            {t('ins:referrals.add')}
          </Button>
        )}
      </Stack>
      <DataTable
        captionKey="ins:referrals.caption"
        columns={columns}
        rows={referrals.data}
        getRowId={(row) => row.referralId}
        isLoading={referrals.isPending}
        error={referrals.error}
        onRetry={() => void referrals.refetch()}
        emptyTitleKey="ins:referrals.empty"
      />
      {creating && <ReferralDialog patientId={patientId} onClose={() => setCreating(false)} />}
    </>
  );
}

const referralSchema = z.object({
  issuer: zRequiredText(200),
  number: zRequiredText(64),
  issuedOn: z.string().min(1, 'common:validation.required'),
  expiresOn: z.string().min(1, 'common:validation.required'),
  amountLimit: z.number().nullable(),
  usage: z.string(),
});

type ReferralForm = z.input<typeof referralSchema>;

function ReferralDialog({ patientId, onClose }: { patientId: string; onClose: () => void }) {
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const [submitError, setSubmitError] = useState<unknown>();
  const form = useForm<ReferralForm>({
    resolver: zodResolver(referralSchema),
    defaultValues: { issuer: '', number: '', issuedOn: '', expiresOn: '', amountLimit: null, usage: 'SingleUse' },
  });

  const save = useMutation({
    mutationFn: (values: ReferralForm) =>
      unwrap(
        tenantApi.POST('/api/v1/insurance/referral-letters', {
          headers: { 'Idempotency-Key': idempotency.current() },
          body: {
            patientId,
            coverageId: null,
            issuer: values.issuer,
            number: values.number,
            issuedOn: values.issuedOn,
            expiresOn: values.expiresOn,
            serviceIds: [],
            amountLimit: values.amountLimit && values.amountLimit > 0 ? values.amountLimit : null,
            usage: values.usage as InsReferralUsage,
            documentId: null,
          },
        }),
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: insuranceKeys.referrals(session?.tenantId, patientId) });
      onClose();
    },
    onError: (error) => {
      const applied = applyServerErrors(error, form.setError, { fields: ['issuer', 'number', 'expiresOn', 'amountLimit'] });
      setSubmitError(applied > 0 ? undefined : error);
    },
  });

  return (
    <FormDialog
      open
      titleKey="ins:referrals.add_title"
      onClose={onClose}
      onSubmit={form.handleSubmit((values) => save.mutate(values))}
      isDirty={form.formState.isDirty}
      isSubmitting={save.isPending}
      submitError={submitError}
    >
      <Stack spacing={2}>
        <TextFieldRHF control={form.control} name="issuer" labelKey="ins:referrals.fields.issuer" data-testid="ins-referral-issuer" />
        <TextFieldRHF control={form.control} name="number" labelKey="ins:referrals.fields.number" data-testid="ins-referral-number" />
        <span data-testid="ins-referral-issued-on">
          <JalaliDatePickerRHF control={form.control} name="issuedOn" labelKey="ins:referrals.fields.issued_on" />
        </span>
        <span data-testid="ins-referral-expires-on">
          <JalaliDatePickerRHF control={form.control} name="expiresOn" labelKey="ins:referrals.fields.expires_on" />
        </span>
        <span data-testid="ins-referral-limit">
          <MoneyFieldRHF
            control={form.control}
            name="amountLimit"
            labelKey="ins:referrals.fields.amount_limit"
            helperKey="ins:referrals.fields.amount_limit_hint"
            fullWidth
          />
        </span>
        <SelectRHF
          control={form.control}
          name="usage"
          labelKey="ins:referrals.fields.usage"
          options={REFERRAL_USAGES.map((usage) => ({ value: usage, labelKey: `ins:referral_usage.${usage}` }))}
          data-testid="ins-referral-usage"
        />
      </Stack>
    </FormDialog>
  );
}
